import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Crosshair, Waves, Zap, ArrowDownToLine, Repeat, Flame, Shuffle, SlidersHorizontal, ChevronDown, Sun, Moon, Clock, Globe2, Building2, Trash2, Layers3, Hammer } from "lucide-react";
import { toast } from "sonner";
import { useSim } from "../lib/store";
import { Seismograph } from "./Seismograph";

const PRESETS = [
  { id: "scenario-preset-1906-sf", label: "1906 San Francisco", mag: 7.9, dur: 60, depth: 8, lat: 37.75, lon: -122.55 },
  { id: "scenario-preset-2011-tohoku", label: "2011 Tohoku", mag: 9.1, dur: 150, depth: 29, lat: 38.297, lon: 142.373 },
  { id: "scenario-preset-2023-turkey", label: "2023 Turkey", mag: 7.8, dur: 80, depth: 18, lat: 37.166, lon: 37.042 },
];

const VOLCANO_PRESETS = [
  { id: "volcano-preset-krakatoa", label: "Krakatoa", mag: 8.2, dur: 90, lat: -6.1, lon: 105.42 },
  { id: "volcano-preset-vesuvius", label: "Vesuvius", mag: 7.4, dur: 70, lat: 40.82, lon: 14.43 },
];

function magColor(m) {
  if (m >= 8) return "#dc2626";
  if (m >= 6.5) return "#f97316";
  return "#eab308";
}

export function ControlDeck() {
  const {
    magnitude, duration, events, selectedId, epicenter, simulating, tsunamiEnabled, canEnterStreet, includeAftershocks,
    randomize, randomizing, clickStage, viewMode, timeOfDay, autoCycle, liquefaction,
    setMagnitude, setDuration, setTsunamiEnabled, setIncludeAftershocks,
    setRandomize, resetEpicenterFlow, executeAll, stopSim, setViewMode, rebuildCity,
    setTimeOfDay, setAutoCycle, setLiquefaction, setDayPreset,
    addEvent, removeEvent, setSelectedId, setFocusEvent,
  } = useSim();

  const magPct = ((magnitude - 3) / (9.5 - 3)) * 100;
  const durPct = ((duration - 10) / (300 - 10)) * 100;
  const locked = randomize;
  const full = events.length >= 3;

  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) setOpen(false);
  }, []);

  const runAll = () => {
    if (simulating) return stopSim();
    if (!events.length) return toast.error("Stage at least one event — click the globe to drop a pin");
    if (randomize) {
      useSim.setState({ randomizing: true });
      setTimeout(() => {
        const st = useSim.getState();
        st.events.forEach((e) =>
          st.updateEvent(e.id, {
            magnitude: Math.round((3 + Math.random() * 6.5) * 10) / 10,
            duration: Math.round(15 + Math.random() * 165),
          })
        );
        useSim.setState({ randomizing: false });
        useSim.getState().executeAll();
      }, 2000);
      return;
    }
    executeAll();
    const erupting = useSim.getState().events.filter((e) => e.activeEruption);
    toast(`Executing ${events.length} event${events.length > 1 ? "s" : ""}`, {
      description: events.some((e) => e.offshore) ? "Offshore source present — tsunami wavefront engaged" : "Ground acceleration dispersal engaged",
    });
    if (erupting.length) {
      toast.warning(`Volcanic coupling: ${erupting.map((e) => e.activeEruption.name).join(", ")}`, {
        description: "Mw 7.0+ ruptures unclamped magma chambers — eruptions triggered.",
      });
    }
  };

  const stageQuake = (p) => {
    if (full) return toast.error("Max 3 events staged — remove one first");
    addEvent({ lat: p.lat, lon: p.lon, magnitude: p.mag, duration: p.dur, type: "seismic" });
    toast.success(`Staged ${p.label}`);
  };

  const stageVolcano = (v) => {
    if (full) return toast.error("Max 3 events staged — remove one first");
    addEvent({ lat: v.lat, lon: v.lon, magnitude: v.mag, duration: v.dur, type: "volcanic", volcanoName: v.label });
    toast.warning(`Staged ${v.label} (volcanic)`, { description: "Press EXECUTE ALL to launch." });
  };

  return (
    <>
      <button
        data-testid="deck-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 px-3 py-2.5 rounded-full bg-zinc-950/85 backdrop-blur-xl border border-white/15 text-zinc-200 hover:text-white shadow-[0_8px_30px_rgba(0,0,0,0.7)] transition-colors"
        title={open ? "Hide parameters" : "Show parameters"}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />}
        <span className="text-xs font-semibold hidden sm:inline">{open ? "Hide" : "Controls"}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 30, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 30, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="control-deck-panel"
            className="fixed bottom-20 right-4 z-40 w-[92vw] max-w-sm lg:w-[26vw] lg:min-w-[380px] lg:max-w-[460px] max-h-[82vh] overflow-y-auto p-4 rounded-2xl bg-zinc-950/85 backdrop-blur-2xl border border-white/15 shadow-[0_20px_50px_rgba(0,0,0,0.9),0_0_30px_rgba(239,68,68,0.08)]"
          >
            <AnimatePresence>
              {canEnterStreet && (
                <motion.button
                  data-testid="enter-street-prompt"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  onClick={() => setViewMode("street")}
                  className="absolute -top-14 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/90 text-white text-xs font-semibold border border-red-300/40 pulse-glow whitespace-nowrap"
                >
                  <ArrowDownToLine className="h-4 w-4" /> Zoom in detected — Enter 1:1 Street Level
                </motion.button>
              )}
            </AnimatePresence>

            {/* status line */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2 font-mono-tele text-[11px] text-zinc-400">
                {epicenter ? (
                  <>
                    <span className="text-red-400" data-testid="epicenter-coords">
                      LAT {epicenter.lat.toFixed(2)}° / LON {epicenter.lon.toFixed(2)}°
                    </span>
                    <span className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider ${epicenter.offshore ? "border-cyan-500/40 text-cyan-300 bg-cyan-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`}>
                      {epicenter.offshore ? "Offshore" : "Onshore"}
                    </span>
                  </>
                ) : (
                  <span className="text-zinc-500 uppercase tracking-wider">No events — click the globe to place a pin</span>
                )}
              </div>
              <span data-testid="click-stage-hint" className="font-mono-tele text-[10px] uppercase tracking-wider text-zinc-500">
                {clickStage === 0 && (full ? "3/3 slots full — remove one" : `${events.length}/3 staged — click globe to add`)}
                {clickStage === 1 && "GOLD draft — click again to STAGE · Delete to cancel"}
              </span>
            </div>

            {/* Multi-Event Technical Workbench */}
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] font-bold uppercase tracking-wide text-zinc-100 flex items-center gap-1.5">
                  <Layers3 className="h-3.5 w-3.5 text-red-400" /> Current Scenario
                </h3>
                <span className="font-mono-tele text-[10px] text-zinc-500">{events.length}/3</span>
              </div>

              {events.length === 0 ? (
                <div data-testid="event-list-empty" className="text-[11px] text-zinc-500 py-2 px-1">
                  Drop up to 3 epicenters, then <span className="text-red-300 font-semibold">Execute All</span> to run them simultaneously.
                </div>
              ) : (
                <div className="flex flex-col gap-1.5" data-testid="event-list">
                  {events.map((e, i) => (
                    <div
                      key={e.id}
                      data-testid={`event-slot-${e.id}`}
                      onClick={() => setSelectedId(e.id)}
                      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 border cursor-pointer transition-all ${
                        selectedId === e.id ? "bg-red-950/40 border-red-500/50 ring-1 ring-red-500/30" : "bg-white/[0.03] border-white/10 hover:border-white/20"
                      }`}
                    >
                      {e.type === "volcanic" ? <Flame className="h-3.5 w-3.5 text-orange-400 shrink-0" /> : <Crosshair className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-mono-tele text-[11px] text-zinc-200 truncate">
                          {e.type === "volcanic" ? e.volcanoName : `Event ${i + 1}`}
                          <span className="ml-1.5 font-bold" style={{ color: magColor(e.magnitude) }}>Mw {e.magnitude.toFixed(1)}</span>
                        </div>
                        <div className="font-mono-tele text-[9px] text-zinc-500 truncate">
                          {e.duration.toFixed(0)}s · {e.lat.toFixed(1)},{e.lon.toFixed(1)} · {e.offshore ? "offshore" : "onshore"}
                        </div>
                      </div>
                      <button
                        data-testid={`event-focus-${e.id}`}
                        onClick={(ev) => { ev.stopPropagation(); setFocusEvent(e.id); }}
                        title="Fly 1:1 street camera to this event"
                        className="shrink-0 p-1.5 rounded-md bg-white/5 hover:bg-red-600/60 text-zinc-300 hover:text-white transition-colors"
                      >
                        <Building2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`event-delete-${e.id}`}
                        onClick={(ev) => { ev.stopPropagation(); removeEvent(e.id); }}
                        title="Remove event"
                        className="shrink-0 p-1.5 rounded-md bg-white/5 hover:bg-red-950/60 text-zinc-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                data-testid="execute-all-button"
                onClick={runAll}
                disabled={!events.length && !simulating}
                className={`mt-2.5 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white border transition-all ${
                  simulating
                    ? "bg-zinc-800 border-white/20"
                    : events.length
                    ? "bg-gradient-to-r from-red-600 to-red-700 border-red-400/40 shadow-[0_0_25px_rgba(239,68,68,0.5)] hover:shadow-[0_0_35px_rgba(239,68,68,0.8)] hover:from-red-500 hover:to-red-600 active:scale-[0.98]"
                    : "bg-zinc-800/60 border-white/10 opacity-50 cursor-not-allowed"
                }`}
              >
                {simulating ? <Zap className="h-4 w-4 animate-pulse" /> : <Play className="h-4 w-4" />}
                {simulating ? "Stop All Ruptures" : `Execute All (${events.length})`}
              </button>
            </div>

            {/* Seismic Parameters (edit the selected event) */}
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-100">
                  Seismic Parameters {selectedId && <span className="ml-1 text-[10px] font-mono-tele text-red-300">· editing selected</span>}
                </h3>
                <button
                  data-testid="randomize-toggle"
                  onClick={() => setRandomize(!randomize)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
                    randomize ? "bg-fuchsia-950/40 border-fuchsia-500/50 text-fuchsia-200" : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  <Shuffle className="h-3.5 w-3.5" /> Randomize
                </button>
              </div>

              {randomizing ? (
                <div data-testid="seismograph-reveal" className="py-1">
                  <Seismograph />
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-bold uppercase tracking-wide text-zinc-200">Moment Magnitude (Mw){locked && <span className="ml-2 text-[10px] font-mono-tele text-fuchsia-300">LOCKED</span>}</label>
                      <span data-testid="magnitude-value-readout" className="font-mono-tele text-xl font-bold tabular-nums drop-shadow-[0_0_12px_rgba(239,68,68,0.4)]" style={{ color: magColor(magnitude) }}>
                        {magnitude.toFixed(1)}
                      </span>
                    </div>
                    <input
                      data-testid="magnitude-slider"
                      type="range" min={3} max={9.5} step={0.1} value={magnitude} disabled={locked}
                      onChange={(e) => setMagnitude(parseFloat(e.target.value))}
                      className="seismic-range disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: `linear-gradient(to right, #eab308 0%, #f97316 ${magPct * 0.6}%, #dc2626 ${magPct}%, rgba(39,39,42,0.85) ${magPct}%)` }}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-bold uppercase tracking-wide text-zinc-200">Rupture Duration{locked && <span className="ml-2 text-[10px] font-mono-tele text-fuchsia-300">LOCKED</span>}</label>
                      <span data-testid="duration-value-readout" className="font-mono-tele text-xl font-bold text-red-500 tabular-nums drop-shadow-[0_0_12px_rgba(239,68,68,0.4)]">
                        {duration.toFixed(0)}s
                      </span>
                    </div>
                    <input
                      data-testid="duration-slider"
                      type="range" min={10} max={300} step={5} value={duration} disabled={locked}
                      onChange={(e) => setDuration(parseFloat(e.target.value))}
                      className="seismic-range disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: `linear-gradient(to right, #dc2626 0%, #dc2626 ${durPct}%, rgba(39,39,42,0.85) ${durPct}%)` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* View Analysis + Environment */}
            <div className="mt-3 rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-300 mb-1.5">View Analysis</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="view-analysis-globe"
                    onClick={() => setViewMode("macro")}
                    className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${viewMode === "macro" ? "bg-red-600/90 text-white border-red-400/40" : "bg-white/5 text-zinc-300 border-white/10"}`}
                  >
                    <Globe2 className="h-3.5 w-3.5" /> Global Globe
                  </button>
                  <button
                    data-testid="view-analysis-street"
                    onClick={() => { if (!events.length) return toast.error("Stage an event first"); setViewMode("street"); }}
                    className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${viewMode === "street" ? "bg-red-600/90 text-white border-red-400/40" : "bg-white/5 text-zinc-300 border-white/10"} ${!events.length ? "opacity-40" : ""}`}
                  >
                    <Building2 className="h-3.5 w-3.5" /> 1:1 Sandbox
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-bold uppercase tracking-wide text-zinc-300">Soil Liquefaction</label>
                  <span data-testid="liquefaction-readout" className="font-mono-tele text-xs font-bold text-cyan-300">{Math.round(liquefaction * 100)}%</span>
                </div>
                <input
                  data-testid="liquefaction-slider"
                  type="range" min={0} max={1} step={0.05} value={liquefaction}
                  onChange={(e) => setLiquefaction(parseFloat(e.target.value))}
                  className="seismic-range"
                  style={{ background: `linear-gradient(to right, #22d3ee 0%, #0891b2 ${liquefaction * 100}%, rgba(39,39,42,0.85) ${liquefaction * 100}%)` }}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-300 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Lighting</div>
                  <span data-testid="time-readout" className="font-mono-tele text-xs font-bold text-amber-300">{String(Math.floor(timeOfDay)).padStart(2, "0")}:00</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <button data-testid="lighting-sunny" onClick={() => setDayPreset("sunny")} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border ${timeOfDay >= 6 && timeOfDay <= 18.5 ? "bg-amber-500/20 border-amber-400/50 text-amber-200" : "bg-white/5 border-white/10 text-zinc-400"}`}><Sun className="h-3.5 w-3.5" /> Sunny</button>
                  <button data-testid="lighting-dark" onClick={() => setDayPreset("dark")} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border ${timeOfDay > 18.5 || timeOfDay < 6 ? "bg-indigo-500/20 border-indigo-400/50 text-indigo-200" : "bg-white/5 border-white/10 text-zinc-400"}`}><Moon className="h-3.5 w-3.5" /> Dark</button>
                  <button data-testid="lighting-autocycle" onClick={() => setAutoCycle(!autoCycle)} className={`ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border ${autoCycle ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-200" : "bg-white/5 border-white/10 text-zinc-400"}`}><Repeat className="h-3.5 w-3.5" /> Auto</button>
                </div>
                <input
                  data-testid="time-of-day-slider"
                  type="range" min={6} max={24} step={0.5} value={timeOfDay}
                  onChange={(e) => { setAutoCycle(false); setTimeOfDay(parseFloat(e.target.value)); }}
                  className="seismic-range"
                  style={{ background: `linear-gradient(to right, #f59e0b 0%, #fbbf24 40%, #1e3a8a 100%)` }}
                />
              </div>
            </div>

            {/* action bar */}
            <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-white/10 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  data-testid="clear-simulation"
                  onClick={() => { resetEpicenterFlow(); setViewMode("macro"); toast("Scenario cleared"); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border bg-white/5 border-white/10 text-zinc-300 hover:text-white transition-all"
                >
                  <Zap className="h-4 w-4" /> Clear All
                </button>
                <button
                  data-testid="rebuild-city"
                  onClick={() => { rebuildCity(); toast.success("City rebuilt", { description: "Structures restored — re-run your scenario when ready." }); }}
                  disabled={!events.length}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                    events.length ? "bg-emerald-950/40 border-emerald-500/50 text-emerald-200 hover:bg-emerald-900/50" : "bg-white/5 border-white/10 text-zinc-500 opacity-40 cursor-not-allowed"
                  }`}
                  title="Restore the ruined city so you can re-run the scenario"
                >
                  <Hammer className="h-4 w-4" /> Rebuild
                </button>
                <button
                  data-testid="tsunami-wavefront-toggle"
                  onClick={() => setTsunamiEnabled(!tsunamiEnabled)}
                  disabled={!epicenter?.offshore}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                    tsunamiEnabled && epicenter?.offshore ? "bg-cyan-950/40 border-cyan-500/50 text-cyan-200" : "bg-white/5 border-white/10 text-zinc-400"
                  } ${!epicenter?.offshore ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <Waves className="h-4 w-4" /> Tsunami
                </button>
                <button
                  data-testid="aftershock-toggle"
                  onClick={() => setIncludeAftershocks(!includeAftershocks)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                    includeAftershocks ? "bg-orange-950/40 border-orange-500/50 text-orange-200" : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  <Repeat className="h-4 w-4" /> Aftershocks
                </button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {PRESETS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={p.id}
                    onClick={() => stageQuake(p)}
                    className="flex items-center px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] text-zinc-300 hover:text-white border border-white/10 transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
                {VOLCANO_PRESETS.map((v) => (
                  <button
                    key={v.id}
                    data-testid={v.id}
                    onClick={() => stageVolcano(v)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-orange-950/40 hover:bg-orange-900/50 text-[11px] text-orange-200 border border-orange-500/40 transition-colors"
                  >
                    <Flame className="h-3 w-3" /> {v.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
